import { isProductReadinessEnabled } from "@/utils/accountProductFlags";

export const EMPLOYEE_PREPARE_COLUMN_VALUE = "prepares";
export const POSITION_PREPARE_COLUMN_VALUE = "preparingFor";
export const PREPARING_FOR_FILTER_LABEL = "Preparing for";
export const PREPARING_FOR_COMMITMENT = "Preparing for";

export const EMPLOYEE_SEARCH_PLACEHOLDER_READINESS_ON =
  "Search by Name, Primary Position, Preparing For Position";
export const EMPLOYEE_SEARCH_PLACEHOLDER_READINESS_OFF =
  "Search by Name, Primary Position";

const PREPARING_EMPLOYEE_FILTER_VALUES = new Set([
  "prepares",
  "does_not_prepare",
  "Prepares for",
  "Does not prepare for",
]);

const PREPARING_POSITION_FILTER_VALUES = new Set([
  "has preparing for",
  "has_preparing",
  "nobody prepares for",
  "no_preparing",
  "Has Preparing for",
  "Nobody prepares for",
]);

const POSITION_VALIDITY_FILTER_VALUES = new Set([
  "is valid",
  "is not valid",
  "Is valid",
  "Is not valid",
]);

const POSITION_ROLES_ATTACHED_FILTER_VALUES = new Set([
  "attached",
  "no role attached",
  "Attached",
  "No role attached",
]);

/** @returns {boolean} */
export function isPreparingForOrganizationUiEnabled() {
  return isProductReadinessEnabled();
}

/**
 * @param {Array<{ value?: string, component?: string }>} headers
 * @param {Array<{ component?: string }>} columns
 */
export function withoutPreparingEmployeeTableColumns(headers, columns) {
  if (isPreparingForOrganizationUiEnabled()) {
    return { headers, columns };
  }
  return {
    headers: headers.filter((h) => h.value !== EMPLOYEE_PREPARE_COLUMN_VALUE),
    columns: columns.filter((c) => c.component !== "ExEmployeePrepareAssignmentCard"),
  };
}

/**
 * @param {Array<{ value?: string }>} headers
 * @param {Array<{ component?: string }>} columns
 */
export function withoutPreparingPositionTableColumns(headers, columns) {
  if (isPreparingForOrganizationUiEnabled()) {
    return { headers, columns };
  }
  return {
    headers: headers.filter((h) => h.value !== POSITION_PREPARE_COLUMN_VALUE),
    columns: columns.filter((c) => c.component !== "PreparingForPositionAssignmentCard"),
  };
}

/** @param {Array<{ key: string, label: string }>} fields */
export function getEmployeeMultiFieldSearchFields(fields) {
  const list = Array.isArray(fields) ? fields : [];
  if (isPreparingForOrganizationUiEnabled()) return list;
  return list.filter((f) => f.key !== "preparing_position_name");
}

/** @param {Array<{ key: string, label: string }>} fields */
export function getPositionMultiFieldSearchFields(fields) {
  const list = Array.isArray(fields) ? fields : [];
  if (isPreparingForOrganizationUiEnabled()) return list;
  return list.filter((f) => f.key !== "preparing_employee_name");
}

export function getEmployeeSearchPlaceholder() {
  return isPreparingForOrganizationUiEnabled()
    ? EMPLOYEE_SEARCH_PLACEHOLDER_READINESS_ON
    : EMPLOYEE_SEARCH_PLACEHOLDER_READINESS_OFF;
}

/** @param {Array<{ label: string }>} filterGroups */
export function filterPreparingForFromPositionFilterGroups(filterGroups) {
  const groups = Array.isArray(filterGroups) ? filterGroups : [];
  if (isPreparingForOrganizationUiEnabled()) return groups;
  return groups.filter((g) => g.label !== PREPARING_FOR_FILTER_LABEL);
}

/** @param {Array<{ label: string }>} filterGroups */
export function filterValidityFromPositionFilterGroups(filterGroups) {
  const groups = Array.isArray(filterGroups) ? filterGroups : [];
  if (isPreparingForOrganizationUiEnabled()) return groups;
  return groups.filter((g) => g.label !== "Validity");
}

export function stripValidityPositionFilterValues(values) {
  const list = Array.isArray(values) ? values : [];
  if (isPreparingForOrganizationUiEnabled()) return list;
  return list.filter((el) => {
    const raw = typeof el === "string" ? el : el?.value || el?.label || "";
    const normalized = String(raw).toLowerCase();
    return (
      !POSITION_VALIDITY_FILTER_VALUES.has(normalized) &&
      !POSITION_VALIDITY_FILTER_VALUES.has(raw)
    );
  });
}

export function stripRolesAttachedPositionFilterValues(values) {
  const list = Array.isArray(values) ? values : [];
  if (isPreparingForOrganizationUiEnabled()) return list;
  return list.filter((el) => {
    const raw = typeof el === "string" ? el : el?.value || el?.label || "";
    const normalized = String(raw).toLowerCase();
    return (
      !POSITION_ROLES_ATTACHED_FILTER_VALUES.has(normalized) &&
      !POSITION_ROLES_ATTACHED_FILTER_VALUES.has(raw)
    );
  });
}

/** @param {Array<string|object>} values */
/** @param {Array<string|object>} values */
export function stripPreparesEmployeeFilterValues(values) {
  const list = Array.isArray(values) ? values : [];
  if (isPreparingForOrganizationUiEnabled()) return list;
  return list.filter((el) => {
    const raw = typeof el === "string" ? el : el?.value || el?.label || "";
    const normalized = String(raw).toLowerCase();
    return (
      !PREPARING_EMPLOYEE_FILTER_VALUES.has(normalized) &&
      !PREPARING_EMPLOYEE_FILTER_VALUES.has(raw)
    );
  });
}

export function stripPreparingForPositionFilterValues(values) {
  const list = Array.isArray(values) ? values : [];
  if (isPreparingForOrganizationUiEnabled()) return list;
  return list.filter((el) => {
    const raw = typeof el === "string" ? el : el?.value || el?.label || "";
    const normalized = String(raw).toLowerCase();
    return !PREPARING_POSITION_FILTER_VALUES.has(normalized) && !PREPARING_POSITION_FILTER_VALUES.has(raw);
  });
}

/** Positions list (grid) view — the only Organization > Positions view when readiness is off. */
export const ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION = "Grid";

const NON_LIST_ORGANIZATION_POSITIONS_VIEW_SELECTIONS = new Set(["Pane", "Categorized"]);

/** Tree / categorized deep links and pane drill-down query keys on Organization > Positions. */
export const ORGANIZATION_POSITIONS_NON_LIST_ROUTE_QUERY_KEYS = Object.freeze([
  "positionId",
  "roleId",
  "resId",
  "positionTab",
  "roleTab",
  "resTab",
  "positionName",
  "roleCode",
  "roleName",
  "jobCategoryId",
  "view",
]);

/** @returns {boolean} When false, Organization > Positions is list-only (no view toggle). */
export function isOrganizationPositionsMultiViewEnabled() {
  return isPreparingForOrganizationUiEnabled();
}

/** @returns {boolean} */
export function shouldShowOrganizationPositionsViewSelector() {
  return isOrganizationPositionsMultiViewEnabled();
}

/**
 * @param {string|undefined|null} selection PositionsViewSelector value (Grid | Pane | Categorized)
 * @returns {string}
 */
export function normalizeOrganizationPositionsViewSelection(selection) {
  const value = selection || ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION;
  if (
    !isOrganizationPositionsMultiViewEnabled() &&
    NON_LIST_ORGANIZATION_POSITIONS_VIEW_SELECTIONS.has(value)
  ) {
    return ORGANIZATION_POSITIONS_LIST_VIEW_SELECTION;
  }
  return value;
}

function organizationPositionsTabSlug(tabParam) {
  return String(tabParam || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
}

/** @param {{ name?: string, params?: { tab?: string } }} route */
export function isOrganizationPositionsOrganizationRoute(route) {
  if (!route || route.name !== "NewOrganization") return false;
  return organizationPositionsTabSlug(route.params?.tab) === "positions";
}

/** @param {Record<string, unknown>|undefined|null} query */
export function isOrganizationPositionsNonListRouteQuery(query) {
  if (!query || typeof query !== "object") return false;
  if (
    ORGANIZATION_POSITIONS_NON_LIST_ROUTE_QUERY_KEYS.some((key) => {
      if (key === "view") return false;
      const val = query[key];
      return val != null && String(val) !== "";
    })
  ) {
    return true;
  }
  const view = String(query.view || "").toLowerCase();
  return view === "pane" || view === "tree" || view === "categorized";
}

/** @param {Record<string, unknown>|undefined|null} query */
export function stripOrganizationPositionsNonListRouteQuery(query) {
  const next = { ...(query || {}) };
  ORGANIZATION_POSITIONS_NON_LIST_ROUTE_QUERY_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}

/**
 * When readiness is off, rewrite Organization > Positions URLs that target tree or categorized views.
 * @returns {boolean} true if router.replace was attempted
 */
export function redirectOrganizationPositionsToListViewIfNeeded(router, route) {
  if (!isOrganizationPositionsMultiViewEnabled() && router && route) {
    if (!isOrganizationPositionsOrganizationRoute(route)) return false;
    if (!isOrganizationPositionsNonListRouteQuery(route.query)) return false;
    const query = stripOrganizationPositionsNonListRouteQuery(route.query);
    router.replace({ name: route.name, params: route.params, query }).catch(() => {});
    return true;
  }
  return false;
}
